import { useState, useEffect, useRef } from 'react';
import { openSnackbar } from "../../../api/snackbar";
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useFormik } from 'formik';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Step,
  Stepper,
  StepLabel,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Stack,
  CircularProgress,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import {
  requestAbhaOtp,
  verifyAbhaOtp,
  formatAbhaProfile,
  generateAbhaSuggestions,
  registerAbhaAddress,
  downloadAbhaCard,
  generateAbhaUpdateMobileOtp,
  verifyAbhaUpdateMobileOtp,
  requestAbhaMobileUpdateOtp,
  verifyAbhaMobileUpdate
} from 'utils/apiHelper';
import {
  ABHA_ADDRESS_RULES,
  ABHA_ADDRESS_SUFFIX,
  INVALID_OTP_MESSAGE,
  MAX_OTP_RESENDS,
  MAX_RESEND_REACHED_MESSAGE,
  OTP_RESEND_SECONDS,
  extractAbhaErrorMessage,
  getAbhaAddressRuleViolations,
  isAadhaarLinkedMobile,
  isFailedAuthResult,
  isSameAbhaMobile,
  maskMobile,
  blobErrorMessage,
  otpVerificationErrorMessage,
  saveAbhaCardBlob
} from 'utils/abhaUiUtils';
import { isAadhaarValid } from 'utils/aadhaarUtils';
import useAuth from 'hooks/useAuth';
import { isProduction } from 'utils/helper';
import { scoreAbhaMatch } from 'utils/abhaMatchUtils';
import AbhaMismatchDialog from 'components/AbhaMismatchDialog';


const steps = [
  'Consent Collections',
  'AADHAAR Authentication',
  'Mobile Verification',
  'ABHA Address Creation'
];

const CONSENT_IMAGE_URL = 'https://sandboxcms.abdm.gov.in/uploads/Consent_Language_ABDM_1_25c394153b.png';
const CONSENT_VERSION = 'ABDM_Consent_Language_1';
const CONSENT_STORAGE_KEY = 'abhaConsentRecords';

const DEFAULT_CONSENT_FLAGS = {
  aadhaarDeclaration: true,
  nonAadhaarDocumentFlow: false,
  legacyRecordLinking: true,
  encounterRecordSharing: true,
  anonymizedPublicHealthUse: true,
  workerExplainedConsent: true,
  beneficiaryConfirmedConsent: false
};

// Covered by "Select All". The non-Aadhaar document statement is optional and
// must be ticked by the beneficiary manually, so it is deliberately excluded.
const SELECT_ALL_CONSENT_KEYS = [
  'aadhaarDeclaration',
  'legacyRecordLinking',
  'encounterRecordSharing',
  'anonymizedPublicHealthUse',
  'workerExplainedConsent',
  'beneficiaryConfirmedConsent'
];

const sanitizeBeneficiaryName = (value) => value.replace(/[^a-zA-Z ]/g, '').replace(/\s{2,}/g, ' ');

const normalizeAbhaAddressInput = (value) => {
  if (typeof value !== 'string') return '';
  return value.trim().toLowerCase().split('@')[0];
};

const cacheAbhaProfileForPatientCreation = (profilePatch = {}) => {
  try {
    const existing = localStorage.getItem('abhaVerifiedProfile');
    const parsed = existing ? JSON.parse(existing) : {};
    localStorage.setItem(
      'abhaVerifiedProfile',
      JSON.stringify({
        ...parsed,
        ...profilePatch
      })
    );
  } catch (error) {
    // Do not block ABHA flow due to localStorage failures.
  }
};

const extractErrorMessage = extractAbhaErrorMessage;

const OTPInput = ({ otp, setOtp }) => {
  const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);

  useEffect(() => {
    setOtp(otpValues.join(''));
  }, [otpValues, setOtp]);

  const handleChange = (index, value) => {
    if (isNaN(value)) return;

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);

    if (value !== '' && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && index > 0 && otpValues[index] === '') {
      inputRefs.current[index - 1].focus();
    }
  };

  return (
    <Stack direction="row" spacing={1} justifyContent="center">
      {otpValues.map((value, index) => (
        <TextField
          key={index}
          inputRef={(ref) => (inputRefs.current[index] = ref)}
          value={value}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          inputProps={{
            maxLength: 1,
            style: { textAlign: 'center' }
          }}
          sx={{ width: '4rem' }}
        />
      ))}
    </Stack>
  );
};

export default function PatientStepper({ open, handleClose, anchorPatient, onAbhaResolved }) {
    const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [agreed, setAgreed] = useState(false);
  const [captchaVerified] = useState(true);
  const [otp, setOtp] = useState('');
  const [mobileOtp, setMobileOtp] = useState('');
  const [isVerifyingMobileOtp, setIsVerifyingMobileOtp] = useState(false);
  const [mobileTxnId, setMobileTxnId] = useState('');
  const [isSendingMobileOtp, setIsSendingMobileOtp] = useState(false);
  const [mobileResendCount, setMobileResendCount] = useState(0);
  const [timer, setTimer] = useState(OTP_RESEND_SECONDS);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [resendCount, setResendCount] = useState(0);
  const [mobileNumber, setMobileNumber] = useState('');
  const [showAadhaar, setShowAadhaar] = useState(false);
  const [resolvedProfile, setResolvedProfile] = useState(null);
  // Aadhaar already had an ABHA: the mobile step updates that ABHA's mobile
  // (profile API) and the address step can keep its address or add a new one.
  const [isExistingAbha, setIsExistingAbha] = useState(false);
  const [useExistingAddress, setUseExistingAddress] = useState(true);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [abhaAddress, setAbhaAddress] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isRegisteringAddress, setIsRegisteringAddress] = useState(false);
  const [isDownloadingCard, setIsDownloadingCard] = useState(false);
  const [txnId, setTxnId] = useState('');
  const [xToken, setXToken] = useState('');
  const [createdAbhaNumber, setCreatedAbhaNumber] = useState('');
  const [createdAbhaAddress, setCreatedAbhaAddress] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [beneficiaryName, setBeneficiaryName] = useState('');
  const [healthcareWorkerName] = useState(user?.first_name || user?.full_name || '');
  const [consentFlags, setConsentFlags] = useState(DEFAULT_CONSENT_FLAGS);

  // Mismatch gate — holds the resolved profile while the dialog is shown
  const [pendingStepperProfile, setPendingStepperProfile] = useState(null);
  const [showStepperMismatch, setShowStepperMismatch] = useState(false);

  const formik = useFormik({
    initialValues: {
      aadhaar: '',
    },
    validationSchema: Yup.object({
      aadhaar: Yup.string()
        .matches(/^\d{12}$/, 'AADHAAR must be 12 digits')
        .test('valid-aadhaar', 'Invalid AADHAAR number', (value) => {
          if (!value) return false;
          return isAadhaarValid(value, { skipChecksum: !isProduction });
        })
        .required('AADHAAR is required'),
    }),
    onSubmit: async (values) => {
      try {
        await handleRequestOtp(values.aadhaar);
      } catch (error) {
        openSnackbar({
          open: true,
          message: error.message || 'Failed to send OTP',
          variant: 'alert',
          alert: {
            color: 'error'
          }
        });
      }
    },
  });

  useEffect(() => {
    let interval;
    if (timer > 0 && (activeStep === 1 || activeStep === 2)) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsResendDisabled(false);
    }
    return () => clearInterval(interval);
  }, [timer, activeStep]);

  const handleNext = async () => {
    try {
      if (activeStep >= steps.length) {
        return;
      }

      if (activeStep === 0) {
        if (agreed && captchaVerified) {
          formik.handleSubmit();
        }
        return;
      }

      if (activeStep === steps.length - 1) {
        if (isExistingAbha && useExistingAddress) {
          finishWithExistingAbha(resolvedProfile);
          return;
        }
        await handleRegisterAbhaAddress();
        return;
      } else {
        setActiveStep((prevStep) => prevStep + 1);
      }
    } catch (error) {
      console.log(error);
      openSnackbar({
        open: true,
        message: error.message || 'An error occurred',
        variant: 'alert',
        alert: {
          color: 'error'
        }
      });
    }
    
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const handleResendOTP = async () => {
    if (resendCount >= MAX_OTP_RESENDS || !formik.values.aadhaar) return;
    setTimer(OTP_RESEND_SECONDS);
    setIsResendDisabled(true);
    setResendCount((prev) => prev + 1);

    try {
      await handleRequestOtp(formik.values.aadhaar, false);
    } catch (error) {
      openSnackbar({
        open: true,
        message: error.message || 'Failed to resend OTP',
        variant: 'alert',
        alert: {
          color: 'error'
        }
      });
    }
  };

  const handleRequestOtp = async (aadhaar, moveToOtpStep = true) => {
    try {
      const consentRecord = buildConsentRecord();
      const response = await requestAbhaOtp(aadhaar, consentRecord);
      if (response.error) {
        throw new Error(response.error.message);
      }
      persistConsentRecord(consentRecord);
      setTxnId(response.txnId);
      setOtpMessage(response.message);
      setIsOtpVerified(false);
      setTimer(OTP_RESEND_SECONDS);
      setIsResendDisabled(true);
      if (moveToOtpStep) {
        setActiveStep((prevStep) => prevStep + 1);
      }

      openSnackbar({
        open: true,
        message: response.message || 'OTP sent successfully',
        variant: 'alert',
        alert: {
          color: 'success'
        }
      });
    } catch (error) {
      openSnackbar({
        open: true,
        message: extractErrorMessage(error, 'Failed to request OTP'),
        variant: 'alert',
        alert: {
          color: 'error'
        }
      });
      throw error;
    }
  };

  const handleVerifyOtp = async (otp, mobile) => {
    try {
      const response = await verifyAbhaOtp(txnId, otp, mobile);
      if (isFailedAuthResult(response)) {
        throw new Error(INVALID_OTP_MESSAGE);
      }
      const message = response.message;
      openSnackbar({
        open: true,
        message: message,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        variant: 'alert',
        alert: { color: 'success' },
      });
      const rawProfile = response.ABHAProfile || response.profile;
      const formattedProfile = rawProfile ? formatAbhaProfile(rawProfile) : null;
      // Keep the communication mobile number the user typed. ABDM returns the
      // profile mobile only when it matches the Aadhaar-linked mobile.
      if (formattedProfile?.abhaNumber) {
        setCreatedAbhaNumber(formattedProfile.abhaNumber);
      }
      if (formattedProfile) {
        const fullName = [
          formattedProfile.firstName,
          formattedProfile.middleName,
          formattedProfile.lastName
        ]
          .filter(Boolean)
          .join(' ')
          .trim();

        cacheAbhaProfileForPatientCreation({
          fullName,
          contact: formattedProfile.mobile || '',
          gender: formattedProfile.gender || '',
          dob: formattedProfile.dateOfBirth?.format ? formattedProfile.dateOfBirth.format('YYYY-MM-DD') : '',
          email: formattedProfile.email || '',
          pincode: formattedProfile.pincode || '',
          address: formattedProfile.address || '',
          village: formattedProfile.village || '',
          abhaNumber: formattedProfile.abhaNumber || '',
          abhaAddress: formattedProfile.abhaAddress || '',
          profilePhoto: rawProfile?.photo || rawProfile?.profilePhoto || ''
        });
      }
      if (response?.tokens?.token) {
        setXToken(response.tokens.token);
      }
      if (response.txnId) {
        setTxnId(response.txnId);
      }
      setIsOtpVerified(true);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const handleVerifyOtpClick = async () => {
    try {
      const response = await handleVerifyOtp(otp, mobileNumber);
      const enrolTxnId = response?.txnId || txnId;

      const existingProfile = response?.ABHAProfile || response?.profile;
      const existingAddress =
        existingProfile?.preferredAbhaAddress ||
        existingProfile?.abhaAddress ||
        (Array.isArray(existingProfile?.phrAddress) ? existingProfile.phrAddress[0] : '') ||
        '';
      // ABDM flags whether this Aadhaar created a new ABHA. A new account can
      // also come back with an auto-generated address, so only fall back to
      // the message / "has an address" when the flag is missing.
      const isExistingAccount =
        typeof response?.isNew === 'boolean'
          ? !response.isNew
          : /already exist/i.test(response?.message || '') || Boolean(existingAddress);

      // Existing ABHA (M1 obs 19 / 30): show it, verify a different
      // communication mobile on that ABHA, then keep its address or add a new
      // one (see continueWithExistingAbha).
      if (isExistingAccount && existingProfile) {
        // This Aadhaar is already enrolled — the person already has an ABHA.
        // Format the profile so we can run the demographic gate.
        const formattedExisting = existingProfile ? formatAbhaProfile(existingProfile) : null;

        if (formattedExisting && anchorPatient) {
          const { isMatch } = scoreAbhaMatch(formattedExisting, anchorPatient);
          if (!isMatch) {
            // Stash and show the mismatch dialog — do NOT jump to success yet.
            setPendingStepperProfile(formattedExisting);
            setShowStepperMismatch(true);
            return;
          }
        }

        // Match confirmed (or no anchor patient)
        await continueWithExistingAbha(formattedExisting, existingAddress, response?.tokens?.token);
        return;
      }

      // Same number as the Aadhaar-linked mobile → already verified by the
      // Aadhaar OTP, go straight to ABHA address creation. Otherwise the
      // communication mobile must be verified with its own OTP.
      if (isAadhaarLinkedMobile(mobileNumber, otpMessage)) {
        setActiveStep(3);
        return;
      }
      setActiveStep(2);
      await sendCommunicationMobileOtp(enrolTxnId);
    } catch (error) {
      console.log(error);
      openSnackbar({
        open: true,
        message: otpVerificationErrorMessage(error),
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        variant: 'alert',
        alert: { color: 'error' },
      });
    }
  };

  /**
   * Receptionist confirmed "Link Anyway" after the stepper mismatch dialog.
   * Proceed to success and fire the resolved callback.
   */
  const handleStepperMismatchLinkAnyway = () => {
    const profile = pendingStepperProfile;
    setPendingStepperProfile(null);
    setShowStepperMismatch(false);

    if (profile) {
      openSnackbar({
        open: true,
        message: 'Override confirmed — ABHA linked to the selected patient.',
        variant: 'alert',
        alert: { color: 'warning' },
      });
      continueWithExistingAbha(profile, profile.abhaAddress || '', xToken);
      return;
    }
    setActiveStep(steps.length);
  };

  /**
   * The Aadhaar already has an ABHA (M1 obs 19 / 30). Show it, then:
   * - a communication mobile different from the ABHA's is verified by OTP and
   *   becomes the ABHA's mobile (so it is on the profile and the card);
   * - the address step keeps the existing ABHA address or creates a new one.
   */
  const continueWithExistingAbha = async (profile, existingAddress, token) => {
    if (!profile) return;
    setIsExistingAbha(true);
    setUseExistingAddress(true);
    setResolvedProfile(profile);
    setCreatedAbhaAddress(existingAddress || profile.abhaAddress || '');
    if (profile.abhaNumber) setCreatedAbhaNumber(profile.abhaNumber);
    if (token) setXToken(token);
    openSnackbar({
      open: true,
      message: existingAddress
        ? `This Aadhaar already has an ABHA (${existingAddress}).`
        : 'This Aadhaar already has an ABHA.',
      variant: 'alert',
      alert: { color: 'info' },
    });

    if (isSameAbhaMobile(mobileNumber, profile.mobile)) {
      setActiveStep(3);
      return;
    }
    setActiveStep(2);
    await sendCommunicationMobileOtp(txnId, { existing: true, token: token || xToken });
  };

  /** Existing ABHA, keeping its address: hand the profile to the form. */
  const finishWithExistingAbha = (profile) => {
    if (profile) onAbhaResolved?.(profile);
    setActiveStep(steps.length);
  };

  /**
   * Receptionist cancelled after the stepper mismatch dialog.
   * Stay on step 1 — they can go back and enter the correct Aadhaar.
   */
  const handleStepperMismatchCancel = () => {
    setPendingStepperProfile(null);
    setShowStepperMismatch(false);
    // Reset OTP input so they can try again
    setIsOtpVerified(false);
    setOtp('');
  };

  const sendCommunicationMobileOtp = async (
    currentTxnId = txnId,
    { existing = isExistingAbha, token = xToken } = {}
  ) => {
    setIsSendingMobileOtp(true);
    try {
      // New ABHA: enrolment mobile OTP. Existing ABHA: change the ABHA's
      // mobile through the profile API (needs the account's token).
      const response = existing
        ? await requestAbhaMobileUpdateOtp({ xToken: token, mobile: mobileNumber })
        : await generateAbhaUpdateMobileOtp(mobileNumber, currentTxnId);
      if (response?.txnId) {
        setMobileTxnId(response.txnId);
      }
      setMobileOtp('');
      setTimer(OTP_RESEND_SECONDS);
      setIsResendDisabled(true);
      openSnackbar({
        open: true,
        message: response?.message || `OTP sent to ${maskMobile(mobileNumber)}`,
        variant: 'alert',
        alert: { color: 'success' }
      });
    } catch (error) {
      openSnackbar({
        open: true,
        message: extractErrorMessage(error, 'Failed to send OTP to communication mobile number'),
        variant: 'alert',
        alert: { color: 'error' }
      });
    } finally {
      setIsSendingMobileOtp(false);
    }
  };

  const handleResendMobileOtp = async () => {
    if (mobileResendCount >= MAX_OTP_RESENDS) return;
    setMobileResendCount((prev) => prev + 1);
    await sendCommunicationMobileOtp();
  };

  const handleVerifyMobileOtp = async () => {
    setIsVerifyingMobileOtp(true);
    try {
      const response = isExistingAbha
        ? await verifyAbhaMobileUpdate({
            xToken,
            txnId: mobileTxnId,
            otp: mobileOtp,
            mobile: mobileNumber,
            abhaNumber: createdAbhaNumber
          })
        : await verifyAbhaUpdateMobileOtp(mobileOtp, mobileTxnId || txnId);
      if (isFailedAuthResult(response)) {
        throw new Error(INVALID_OTP_MESSAGE);
      }
      if (isExistingAbha) {
        // the ABHA now carries the verified communication mobile
        setResolvedProfile((prev) => (prev ? { ...prev, mobile: mobileNumber } : prev));
        setActiveStep(3);
        openSnackbar({ open: true, message: 'Mobile number verified and updated on the ABHA', variant: 'alert', alert: { color: 'success' } });
        return;
      }
      if (response?.txnId) {
        setTxnId(response.txnId);
      }
      openSnackbar({
        open: true,
        message: 'Mobile number verified successfully!',
        variant: 'alert',
        alert: { color: 'success' },
      });
      setActiveStep(3);
    } catch (error) {
      openSnackbar({
        open: true,
        message: otpVerificationErrorMessage(error),
        variant: 'alert',
        alert: { color: 'error' },
      });
    } finally {
      setIsVerifyingMobileOtp(false);
    }
  };

  const handleRegisterAbhaAddress = async () => {
    const normalizedAddress = normalizeAbhaAddressInput(abhaAddress);
    if (!validateAbhaAddress(normalizedAddress) || !txnId) return;

    setIsRegisteringAddress(true);
    try {
      const response = await registerAbhaAddress(normalizedAddress, txnId);
      const resolvedAbhaAddress = response?.abhaAddress || `${normalizedAddress}${ABHA_ADDRESS_SUFFIX}`;
      const resolvedAbhaNumber = response?.ABHANumber || createdAbhaNumber || '';

      setCreatedAbhaAddress(resolvedAbhaAddress);
      if (resolvedAbhaNumber) {
        setCreatedAbhaNumber(resolvedAbhaNumber);
      }

      cacheAbhaProfileForPatientCreation({
        abhaAddress: resolvedAbhaAddress,
        abhaNumber: resolvedAbhaNumber
      });

      openSnackbar({
        open: true,
        message: response.message || 'ABHA address registered successfully',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        variant: 'alert',
        alert: { color: 'success' },
      });

      // Existing ABHA: same person, one more ABHA address
      if (isExistingAbha && resolvedProfile) {
        const updated = { ...resolvedProfile, abhaAddress: resolvedAbhaAddress };
        setResolvedProfile(updated);
        onAbhaResolved?.(updated);
        setActiveStep(steps.length);
        return;
      }

      // Notify the parent (FormAppointmentAdd) so it can sync the new ABHA
      // with the locally-selected patient immediately.
      if (onAbhaResolved) {
        // Build a minimal profile from what we have in state
        const cachedRaw = (() => {
          try { return JSON.parse(localStorage.getItem('abhaVerifiedProfile') || '{}'); }
          catch { return {}; }
        })();
        onAbhaResolved({
          ...cachedRaw,
          abhaAddress: resolvedAbhaAddress,
          abhaNumber: resolvedAbhaNumber,
        });
      }

      setActiveStep(steps.length);
    } catch (error) {
      openSnackbar({
        open: true,
        message: extractErrorMessage(error, 'Failed to register ABHA address'),
        variant: 'alert',
        alert: {
          color: 'error'
        }
      });
    } finally {
      setIsRegisteringAddress(false);
    }
  };

  const handleDownloadCard = async () => {
    if (!xToken) return;
    setIsDownloadingCard(true);
    try {
      const blob = await downloadAbhaCard(xToken);
      await saveAbhaCardBlob(blob);
    } catch (error) {
      openSnackbar({
        open: true,
        message: await blobErrorMessage(error, 'Failed to download ABHA card'),
        variant: 'alert',
        alert: { color: 'error' }
      });
    } finally {
      setIsDownloadingCard(false);
    }
  };

  const buildConsentRecord = () => {
    const now = new Date();
    return {
      version: CONSENT_VERSION,
      source: CONSENT_IMAGE_URL,
      consentCapturedAt: now.toISOString(),
      channel: 'frontend-stepper-aadhaar',
      beneficiaryName: beneficiaryName.trim(),
      healthcareWorkerName: healthcareWorkerName.trim(),
      declarations: {
        aadhaarDeclaration: consentFlags.aadhaarDeclaration,
        nonAadhaarDocumentFlow: consentFlags.nonAadhaarDocumentFlow,
        legacyRecordLinking: consentFlags.legacyRecordLinking,
        encounterRecordSharing: consentFlags.encounterRecordSharing,
        anonymizedPublicHealthUse: consentFlags.anonymizedPublicHealthUse,
        workerExplainedConsent: consentFlags.workerExplainedConsent,
        beneficiaryConfirmedConsent: consentFlags.beneficiaryConfirmedConsent
      }
    };
  };

  const persistConsentRecord = (record) => {
    try {
      const existing = localStorage.getItem(CONSENT_STORAGE_KEY);
      const parsed = existing ? JSON.parse(existing) : [];
      const next = Array.isArray(parsed) ? parsed : [];
      next.push(record);
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(next));
    } catch (error) {
      // Do not block ABHA flow due to client-side persistence issues.
    }
  };

  const isConsentStepValid =
    formik.isValid &&
    agreed &&
    // captchaVerified &&
    consentFlags.aadhaarDeclaration &&
    consentFlags.legacyRecordLinking &&
    consentFlags.encounterRecordSharing &&
    consentFlags.anonymizedPublicHealthUse &&
    consentFlags.workerExplainedConsent &&
    consentFlags.beneficiaryConfirmedConsent &&
    beneficiaryName.trim().length > 0;

  const selectAllChecked = agreed && SELECT_ALL_CONSENT_KEYS.every((key) => consentFlags[key]);
  const selectAllIndeterminate =
    !selectAllChecked && (agreed || SELECT_ALL_CONSENT_KEYS.some((key) => consentFlags[key]));

  const handleSelectAll = (checked) => {
    setConsentFlags((prev) => ({
      ...prev,
      ...Object.fromEntries(SELECT_ALL_CONSENT_KEYS.map((key) => [key, checked]))
    }));
    setAgreed(checked);
  };

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (activeStep !== 3 || !txnId) return;

      try {
        const data = await generateAbhaSuggestions(txnId);
        const suggestions = Array.isArray(data?.abhaAddressList)
          ? data.abhaAddressList.map((item) => item?.split('@')?.[0]).filter(Boolean)
          : [];

        if (suggestions.length) {
          setAddressSuggestions(suggestions.slice(0, 3));
          if (!abhaAddress) {
            setAbhaAddress(suggestions[0]);
          }
        }
      } catch (error) {
        setAddressSuggestions([]);
      }
    };

    fetchSuggestions();
  }, [activeStep, txnId]);

  useEffect(() => {
    if (!open) return;

    setActiveStep(0);
    setAgreed(false);
    setConsentFlags(DEFAULT_CONSENT_FLAGS);
    setBeneficiaryName('');
    setShowAadhaar(false);
    setOtp('');
    setMobileOtp('');
    setMobileTxnId('');
    setMobileResendCount(0);
    setResolvedProfile(null);
    setIsExistingAbha(false);
    setUseExistingAddress(true);
    setTimer(OTP_RESEND_SECONDS);
    setIsResendDisabled(true);
    setResendCount(0);
    setMobileNumber('');
    setIsOtpVerified(false);
    setAbhaAddress('');
    setAddressSuggestions([]);
    setIsRegisteringAddress(false);
    setIsDownloadingCard(false);
    setTxnId('');
    setXToken('');
    setCreatedAbhaNumber('');
    setCreatedAbhaAddress('');
    setOtpMessage('');
    setPendingStepperProfile(null);
    setShowStepperMismatch(false);
    formik.resetForm();
  }, [open]);

  const validateAbhaAddress = (address) => {
    const normalizedAddress = normalizeAbhaAddressInput(address);
    const regex = /^[a-zA-Z0-9]+([._][a-zA-Z0-9]+)*$/;
    return normalizedAddress.length >= 8 &&
      normalizedAddress.length <= 18 &&
      regex.test(normalizedAddress) &&
      !normalizedAddress.startsWith('.') &&
      !normalizedAddress.startsWith('_') &&
      !normalizedAddress.endsWith('.') &&
      !normalizedAddress.endsWith('_');
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="AADHAAR Number"
              name="aadhaar"
              type={showAadhaar ? 'text' : 'password'}
              value={formik.values.aadhaar}
              onChange={(e) => formik.setFieldValue('aadhaar', e.target.value.replace(/\D/g, '').slice(0, 12))}
              onBlur={formik.handleBlur}
              error={formik.touched.aadhaar && Boolean(formik.errors.aadhaar)}
              helperText={formik.touched.aadhaar && formik.errors.aadhaar}
              placeholder="Enter 12 digit AADHAAR number"
              autoComplete="off"
              inputProps={{ inputMode: 'numeric', maxLength: 12 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showAadhaar ? 'Mask Aadhaar number' : 'Unmask Aadhaar number'}
                      onClick={() => setShowAadhaar((prev) => !prev)}
                      edge="end"
                    >
                      {showAadhaar ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">I hereby declare that:</Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAllChecked}
                    indeterminate={selectAllIndeterminate}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                }
                label="Select All"
              />
            </Stack>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={consentFlags.aadhaarDeclaration}
                    onChange={(e) => setConsentFlags((prev) => ({ ...prev, aadhaarDeclaration: e.target.checked }))}
                  />
                }
                label={'I am voluntarily sharing my Aadhaar Number / Virtual ID issued by the Unique Identification Authority of India (\u201cUIDAI\u201d), and my demographic information for the purpose of creating an Ayushman Bharat Health Account number (\u201cABHA number\u201d) and Ayushman Bharat Health Account address (\u201cABHA Address\u201d). I authorize NHA to use my Aadhaar number / Virtual ID for performing Aadhaar based authentication with UIDAI as per the provisions of the Aadhaar (Targeted Delivery of Financial and other Subsidies, Benefits and Services) Act, 2016 for the aforesaid purpose. I understand that UIDAI will share my e-KYC details, or response of \u201cYes\u201d with NHA upon successful authentication.'}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={consentFlags.nonAadhaarDocumentFlow}
                    onChange={(e) => setConsentFlags((prev) => ({ ...prev, nonAadhaarDocumentFlow: e.target.checked }))}
                  />
                }
                label={'I intend to create Ayushman Bharat Health Account Number (\u201cABHA number\u201d) and Ayushman Bharat Health Account address (\u201cABHA Address\u201d) using document other than Aadhaar. (Optional)'}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={consentFlags.legacyRecordLinking}
                    onChange={(e) => setConsentFlags((prev) => ({ ...prev, legacyRecordLinking: e.target.checked }))}
                  />
                }
                label="I consent to usage of my ABHA address and ABHA number for linking of my legacy (past) health records and those which will be generated during this encounter."
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={consentFlags.encounterRecordSharing}
                    onChange={(e) => setConsentFlags((prev) => ({ ...prev, encounterRecordSharing: e.target.checked }))}
                  />
                }
                label="I authorize the sharing of all my health records with healthcare provider(s) for the purpose of providing healthcare services to me during this encounter."
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={consentFlags.anonymizedPublicHealthUse}
                    onChange={(e) => setConsentFlags((prev) => ({ ...prev, anonymizedPublicHealthUse: e.target.checked }))}
                  />
                }
                label="I consent to the anonymization and subsequent use of my health records for public health purposes."
              />
              <FormControlLabel
                sx={{ pl: 4 }}
                control={
                  <Checkbox
                    checked={consentFlags.workerExplainedConsent}
                    onChange={(e) => setConsentFlags((prev) => ({ ...prev, workerExplainedConsent: e.target.checked }))}
                  />
                }
                label={`I, ${healthcareWorkerName || '(healthcare worker name)'}, confirm that I have duly informed and explained the beneficiary of the contents of consent for aforementioned purposes.`}
              />
              <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 4 }}>
                <Checkbox
                  checked={consentFlags.beneficiaryConfirmedConsent}
                  onChange={(e) => setConsentFlags((prev) => ({ ...prev, beneficiaryConfirmedConsent: e.target.checked }))}
                  inputProps={{ 'aria-label': 'Beneficiary consent' }}
                />
                <Typography variant="body1">I,</Typography>
                <TextField
                  size="small"
                  value={beneficiaryName}
                  onChange={(e) => setBeneficiaryName(sanitizeBeneficiaryName(e.target.value))}
                  placeholder="Beneficiary name"
                  helperText="Alphabets only, as on Aadhaar"
                  inputProps={{ maxLength: 100, 'aria-label': 'Beneficiary name' }}
                  sx={{ minWidth: 220 }}
                />
                <Typography variant="body1">
                  , have been explained about the consent as stated above and hereby provide my consent for the aforementioned purposes.
                </Typography>
              </Stack>
            </Stack>
            <FormControlLabel
              control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
              label="I confirm that the above consent statements have been read and accepted."
            />
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              {/* <ReCAPTCHA
                sitekey="your-recaptcha-site-key"
                onChange={(value) => setCaptchaVerified(!!value)}
              /> */}
            </Box>
          </Stack>
        );
      case 1:
        return (
          <Stack spacing={3} alignItems="center">
            <Typography variant="h4">Confirm OTP</Typography>
            <Typography variant="body1" textAlign="center">
              {otpMessage || 'We\'ve sent an OTP to the Mobile Number linked with AADHAAR.'}
            </Typography>
            <TextField
              fullWidth
              label="Communication Mobile Number"
              value={mobileNumber}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                setMobileNumber(value);
              }}
              error={mobileNumber.length > 0 && mobileNumber.length !== 10}
              helperText={
                mobileNumber.length > 0 && mobileNumber.length !== 10
                  ? 'Mobile number must be 10 digits'
                  : 'If different from the Aadhaar-linked mobile, it will be verified with a separate OTP'
              }
              placeholder="Enter 10 digit mobile number"
              sx={{ maxWidth: '360px' }}
            />
            <OTPInput otp={otp} setOtp={setOtp} />
            <Typography variant="body2">
              Time remaining: {Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, '0')}
            </Typography>
            <Button
              variant="text"
              onClick={handleResendOTP}
              disabled={isResendDisabled || resendCount >= MAX_OTP_RESENDS}
            >
              Resend OTP
            </Button>
            <Typography variant="caption" color={resendCount >= MAX_OTP_RESENDS ? 'error' : 'text.secondary'}>
              {resendCount >= MAX_OTP_RESENDS
                ? MAX_RESEND_REACHED_MESSAGE
                : `Resend attempts left: ${Math.max(0, MAX_OTP_RESENDS - resendCount)}`}
            </Typography>
            <Button
              variant="contained"
              onClick={handleVerifyOtpClick}
              disabled={otp.length !== 6 || mobileNumber.length !== 10}
            >
              Verify OTP
            </Button>
          </Stack>
        );
      case 2:
        return (
          <Stack spacing={3} alignItems="center">
            <Typography variant="h4">Verify Communication Mobile Number</Typography>
            <Typography variant="body1" textAlign="center">
              We&apos;ve sent an OTP to {maskMobile(mobileNumber)} to link it as your communication mobile number.
            </Typography>
            <OTPInput otp={mobileOtp} setOtp={setMobileOtp} />
            <Typography variant="body2">
              Time remaining: {Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, '0')}
            </Typography>
            <Button
              variant="text"
              onClick={handleResendMobileOtp}
              disabled={isResendDisabled || isSendingMobileOtp || mobileResendCount >= MAX_OTP_RESENDS}
            >
              Resend OTP
            </Button>
            <Typography variant="caption" color={mobileResendCount >= MAX_OTP_RESENDS ? 'error' : 'text.secondary'}>
              {mobileResendCount >= MAX_OTP_RESENDS
                ? MAX_RESEND_REACHED_MESSAGE
                : `Resend attempts left: ${Math.max(0, MAX_OTP_RESENDS - mobileResendCount)}`}
            </Typography>
            <Button
              variant="contained"
              disabled={mobileOtp.length !== 6 || isVerifyingMobileOtp}
              onClick={handleVerifyMobileOtp}
            >
              {isVerifyingMobileOtp ? <CircularProgress size={24} color="inherit" /> : 'Verify Mobile OTP'}
            </Button>
          </Stack>
        );
      case 3: {
        const suggestions = addressSuggestions;
        const ruleViolations = abhaAddress.length > 0 ? getAbhaAddressRuleViolations(abhaAddress) : [];

        return (
          <Stack spacing={3}>
            {isExistingAbha && (
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="h5" sx={{ mb: 1 }}>This Aadhaar already has an ABHA</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  ABHA Number: {createdAbhaNumber || '-'} · Current ABHA Address: {resolvedProfile?.abhaAddress || createdAbhaAddress || '-'}
                </Typography>
                <RadioGroup
                  value={useExistingAddress ? 'existing' : 'new'}
                  onChange={(e) => setUseExistingAddress(e.target.value === 'existing')}
                >
                  <FormControlLabel
                    value="existing"
                    control={<Radio />}
                    label={`Continue with the existing ABHA address${createdAbhaAddress ? ` (${createdAbhaAddress})` : ''}`}
                  />
                  <FormControlLabel value="new" control={<Radio />} label="Create a new ABHA address for this ABHA" />
                </RadioGroup>
              </Box>
            )}
            {(!isExistingAbha || !useExistingAddress) && (
            <>
            <Typography variant="h4">
              Create your unique ABHA Address*
            </Typography>
            <Typography variant="body1">
              ABHA (Ayushman Bharat Health Account) address is a unique username that allows you to share and access your health records digitally. It is
              similar to an email address, but it is only used for health records.
            </Typography>
            <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
              <Typography variant="h6">Validation rules on ABHA Address:</Typography>
              <ul>
                {ABHA_ADDRESS_RULES.map((rule) => (
                  <Typography
                    component="li"
                    variant="body2"
                    key={rule.key}
                    color={ruleViolations.includes(rule.key) ? 'error' : 'inherit'}
                  >
                    {rule.label}
                  </Typography>
                ))}
              </ul>
            </Box>
            <Typography variant="h6">Enter ABHA Address</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                fullWidth
                value={abhaAddress}
                onChange={(e) => setAbhaAddress(normalizeAbhaAddressInput(e.target.value))}
                error={abhaAddress.length > 0 && !validateAbhaAddress(abhaAddress)}
                helperText={
                  abhaAddress.length > 0 && !validateAbhaAddress(abhaAddress)
                    ? 'Invalid ABHA address format - see the rule(s) marked in red above'
                    : `Enter only the username part before ${ABHA_ADDRESS_SUFFIX}`
                }
                placeholder="Enter ABHA address"
              />
              <Typography variant="body1">{ABHA_ADDRESS_SUFFIX}</Typography>
            </Stack>
            {suggestions.length > 0 && (
              <Box>
                <Typography variant="h6">Suggestions:</Typography>
                <Stack direction="row" spacing={2} mt={1} flexWrap="wrap" useFlexGap>
                  {suggestions.map((suggestion) => (
                    <Button
                      key={suggestion}
                      variant="outlined"
                      sx={{ textTransform: 'none' }}
                      onClick={() => setAbhaAddress(normalizeAbhaAddressInput(suggestion))}
                    >
                      {`${normalizeAbhaAddressInput(suggestion)}${ABHA_ADDRESS_SUFFIX}`}
                    </Button>
                  ))}
                </Stack>
              </Box>
            )}
            </>
            )}
          </Stack>
        );
      }
      default:
        return <Typography>Step {activeStep + 1} content</Typography>;
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Add New Patient</DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ width: '100%' }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box sx={{ mt: 2 }}>
            {activeStep === steps.length ? (
              <Box>
                <Typography variant="h5" sx={{ mb: 1.5 }}>
                  {resolvedProfile ? 'Existing ABHA found' : 'ABHA creation completed'}
                </Typography>
                {createdAbhaNumber && (
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    ABHA Number: {createdAbhaNumber}
                  </Typography>
                )}
                {createdAbhaAddress && (
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    ABHA Address: {createdAbhaAddress}
                  </Typography>
                )}
                {resolvedProfile && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1">Existing ABHA profile details</Typography>
                    {[
                      ['Name', [resolvedProfile.firstName, resolvedProfile.middleName, resolvedProfile.lastName].filter(Boolean).join(' ')],
                      ['Gender', resolvedProfile.gender],
                      ['Date of Birth', resolvedProfile.dateOfBirth?.format ? resolvedProfile.dateOfBirth.format('DD-MM-YYYY') : ''],
                      ['Mobile', resolvedProfile.mobile],
                      ['Address', resolvedProfile.address],
                      ['District', resolvedProfile.district],
                      ['State', resolvedProfile.state],
                      ['PIN Code', resolvedProfile.pincode]
                    ]
                      .filter(([, value]) => value)
                      .map(([label, value]) => (
                        <Typography variant="body2" key={label}>
                          {label}: {value}
                        </Typography>
                      ))}
                  </Box>
                )}
                <Stack direction="row" spacing={2}>
                  <Button onClick={handleReset}>Reset</Button>
                  <Button onClick={handleClose} color="secondary">Close</Button>
                  <Button
                    variant="contained"
                    onClick={handleDownloadCard}
                    disabled={!xToken || isDownloadingCard}
                  >
                    {isDownloadingCard ? 'Downloading...' : 'Download ABHA Card'}
                  </Button>
                </Stack>
              </Box>
            ) : (
              <Box>
                {renderStepContent(activeStep)}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="error">
          Cancel
        </Button>
        {activeStep < steps.length && (
          <>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={
                isRegisteringAddress ||
                (activeStep === 0 && !isConsentStepValid) ||
                (activeStep === 1 && !isOtpVerified) ||
                activeStep === 2 ||
                (activeStep === 3 && !(isExistingAbha && useExistingAddress) && !validateAbhaAddress(abhaAddress))
              }
            >
              {activeStep === steps.length - 1 ? (isRegisteringAddress ? 'Saving...' : 'Finish') : 'Next'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>

    {/* Demographic mismatch gate — shown when an already-enrolled Aadhaar
        belongs to someone who doesn't match the selected patient. */}
    <AbhaMismatchDialog
      open={showStepperMismatch}
      localPatient={anchorPatient}
      abhaProfile={pendingStepperProfile}
      onLinkAnyway={handleStepperMismatchLinkAnyway}
      onCancel={handleStepperMismatchCancel}
    />
    </>
  );
}

PatientStepper.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  /** The currently selected local patient used for demographic validation. */
  anchorPatient: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    full_name: PropTypes.string,
    gender: PropTypes.string,
    dob: PropTypes.string,
  }),
  /** Fired when the stepper resolves an ABHA (either found or newly created).
   *  Called with the formatted ABHA profile so the parent can sync_patient. */
  onAbhaResolved: PropTypes.func,
};

OTPInput.propTypes = {
  otp: PropTypes.string.isRequired,
  setOtp: PropTypes.func.isRequired
}; 